import { create } from 'zustand';
import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { chatStore, projectStore } from '@/storage/projectStorePort';

/**
 * The projects someone is building, read through the ProjectStore port.
 *
 * The port is the seam: IndexedDB today, the /me/projects service when it lands, with no
 * screen changing either time. This store is only the React-facing cache around it.
 */
export interface Projects {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;

  loadProjects: (ownerId: string) => Promise<void>;
  loadProject: (id: string, ownerId: string) => Promise<void>;
  createFromDefinition: (
    name: string,
    description: string,
    definition: AppDefinition,
    ownerId: string,
  ) => Promise<Project>;
  updateProject: (
    id: string,
    updates: Partial<Pick<Project, 'name' | 'description' | 'definition' | 'thumbnail' | 'is_published'>>,
    ownerId: string,
  ) => Promise<void>;
  deleteProject: (id: string, ownerId: string) => Promise<void>;
}

export const useProjects = create<Projects>((set, get) => ({
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,

  loadProjects: async (ownerId) => {
    if (!ownerId) {
      set({ projects: [], isLoading: false });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      await projectStore().claimUnowned(ownerId);
      set({ projects: await projectStore().getAll(ownerId), isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Could not load your projects.' });
    }
  },

  loadProject: async (id, ownerId) => {
    set({ isLoading: true, error: null });
    try {
      set({ currentProject: await projectStore().getById(id, ownerId), isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Could not open that project.' });
    }
  },

  createFromDefinition: async (name, description, definition, ownerId) => {
    const project = await projectStore().create({ name, description, definition }, ownerId);
    set((s) => ({ projects: [project, ...s.projects], currentProject: project }));
    return project;
  },

  updateProject: async (id, updates, ownerId) => {
    await projectStore().update(id, updates, ownerId);
    const current = get().currentProject;
    set({
      projects: await projectStore().getAll(ownerId),
      currentProject: current?.id === id ? { ...current, ...updates } : current,
    });
  },

  deleteProject: async (id, ownerId) => {
    await projectStore().delete(id, ownerId);
    await chatStore().deleteByProjectId(id);
    const current = get().currentProject;
    set({
      projects: await projectStore().getAll(ownerId),
      currentProject: current?.id === id ? null : current,
    });
  },
}));
